import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from '../../contexts/TourContext';
import { supabase } from '../../lib/supabase';
import Icon from 'components/AppIcon';

export default function Login() {
  const navigate = useNavigate();
  const { signIn, demoResetting } = useAuth();
  const { resetTour } = useTour();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setEmailNotConfirmed(false);
    setResendSuccess(false);
    if (!email || !password) {
      setError('Por favor completa todos los campos.');
      return;
    }
    setLoading(true);
    try {
      const { data, error: signInError } = await signIn(email, password);
      if (signInError) {
        const msg = signInError?.message || '';
        if (msg?.toLowerCase()?.includes('email not confirmed') || msg?.toLowerCase()?.includes('email_not_confirmed')) {
          setEmailNotConfirmed(true);
          setError('Confirma tu email antes de iniciar sesión. Revisa tu bandeja de entrada.');
        } else {
          setError(msg || 'Error al iniciar sesión. Verifica tus credenciales.');
        }
      } else {
        // Navigate to main dashboard for all users
        const isDemo = data?.user?.email === 'demo@metalguard.com';
        if (isDemo) {
          resetTour();
        }
        navigate('/main-dashboard', { replace: true });
      }
    } catch (err) {
      setError('Error de red. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) return;
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const { error: resendError } = await supabase?.auth?.resend({
        type: 'signup',
        email,
      });
      if (!resendError) {
        setResendSuccess(true);
      }
    } catch (err) {
      // silent
    } finally {
      setResendLoading(false);
    }
  };

  const fillDemo = async () => {
    setEmail('demo@metalguard.com');
    setPassword('demo1234');
    setError('');
    setEmailNotConfirmed(false);
    setResendSuccess(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--color-background)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'var(--color-accent)' }}>
            <Icon name="Shield" size={28} color="white" strokeWidth={2} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">MetalRecovery</h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            Gestión inteligente de chatarra metálica
          </p>
        </div>

        {/* Card */}
        <div className="card p-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-6">Iniciar sesión</h2>

          {error && (
            <div className="p-3 rounded-lg mb-4 border" style={{ background: 'var(--color-error)15', borderColor: 'var(--color-error)40' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} />
                <p className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{error}</p>
              </div>
              {emailNotConfirmed && (
                <div className="mt-2">
                  {resendSuccess ? (
                    <div className="flex items-center gap-1.5">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" strokeWidth={2} />
                      <span className="text-xs" style={{ color: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif' }}>Email de confirmación reenviado.</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleResendConfirmation}
                      disabled={resendLoading}
                      className="text-xs font-medium underline flex items-center gap-1"
                      style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                    >
                      {resendLoading && <Icon name="Loader2" size={12} color="var(--color-accent)" strokeWidth={2} className="animate-spin" />}
                      Reenviar confirmación
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="tu@empresa.com"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" strokeWidth={2} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || demoResetting}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-60"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
            >
              {demoResetting ? (
                <><Icon name="Loader2" size={18} color="white" strokeWidth={2} className="animate-spin" /> Preparando la demo...</>
              ) : loading ? (
                <><Icon name="Loader2" size={18} color="white" strokeWidth={2} className="animate-spin" /> Iniciando sesión...</>
              ) : (
                <><Icon name="LogIn" size={18} color="white" strokeWidth={2} /> Iniciar sesión</>
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <span className="text-sm text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              ¿No tienes cuenta?{' '}
            </span>
            <Link to="/register" className="text-sm font-medium" style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Crear cuenta
            </Link>
          </div>
        </div>

        {/* Demo credentials */}
        <div className="mt-4 p-4 rounded-lg border border-border" style={{ background: 'var(--color-muted)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={14} color="var(--color-accent)" strokeWidth={2} />
            <span className="text-xs font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cuenta de demostración</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            demo@metalguard.com · demo1234
          </p>
          <button
            onClick={fillDemo}
            disabled={loading || demoResetting}
            className="text-xs font-medium px-3 py-1.5 rounded border border-border hover:bg-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            {demoResetting ? (
              <><Icon name="Loader2" size={12} color="var(--color-accent)" strokeWidth={2} className="animate-spin" /> Preparando demo...</>
            ) : (
              'Usar cuenta demo'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
